import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { z } from "zod";
import {
  MeResponseSchema,
  StudentProfileSchema,
  type OnboardingInput,
  type ProfileUpdate,
} from "../../shared/types";
import { useAuth } from "../auth/AuthProvider";
import { apiRequest } from "./client";
import { SCHOOLWORK_KEY } from "./keys";

const ME_KEY = ["me"];

/** Calls the API as the signed-in person, acting on `studentId` (a parent's child) when given. */
export function useApiCall() {
  const { getToken, actingAs } = useAuth();
  return useCallback(
    async <T,>(method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE", path: string, schema: z.ZodType<T>, body?: unknown, studentId = actingAs) =>
      apiRequest(method, path, await getToken(), schema, body, studentId ? { "x-student-id": studentId } : {}),
    [getToken, actingAs],
  );
}

export function useMe() {
  const { user, actingAs } = useAuth();
  const call = useApiCall();
  return useQuery({
    queryKey: [...ME_KEY, user?.uid, actingAs],
    enabled: Boolean(user),
    queryFn: () => call("GET", "/me", MeResponseSchema),
  });
}

function useMeMutation<V>(run: (call: ReturnType<typeof useApiCall>, vars: V) => Promise<unknown>) {
  const call = useApiCall();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: V) => run(call, vars),
    onSuccess: async () => {
      // Profile clubs and sports also appear in the class lists.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ME_KEY }),
        queryClient.invalidateQueries({ queryKey: SCHOOLWORK_KEY }),
      ]);
    },
  });
}

export function useOnboard() {
  return useMeMutation((call, input: OnboardingInput) => call("POST", "/me/onboarding", StudentProfileSchema, input));
}

export function useUpdateProfile() {
  return useMeMutation((call, update: ProfileUpdate) => call("PUT", "/me/profile", StudentProfileSchema, update));
}

/** Parent approves a child's under-13 account. */
export function useApproveChild() {
  return useMeMutation((call, studentId: string) =>
    call("POST", "/parent/approve", StudentProfileSchema, undefined, studentId),
  );
}

export async function recordSession(idToken: string): Promise<void> {
  await apiRequest("POST", "/session", idToken, z.object({ ok: z.boolean() }));
}

/** Settings → Delete my account: removes the account the request acts on (the student's own, or a parent's child). */
export function useDeleteAccount() {
  const call = useApiCall();
  return useMutation({ mutationFn: () => call("DELETE", "/me", z.object({ ok: z.boolean() })) });
}
